import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './authContext';

const BusinessEditsContext = createContext();

export const useBusinessEdits = () => {
  const context = useContext(BusinessEditsContext);
  if (!context) {
    throw new Error('useBusinessEdits must be used within a BusinessEditsProvider');
  }
  return context;
};

export const BusinessEditsProvider = ({ children }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const { makeAuthenticatedRequest, loginType } = useAuth();

  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:9090';

  /**
   * Construct the Base URL
   * Enforced to MASTER account as per current requirements
   */
  const getEditsBaseUrl = useCallback(() => {
    const servicePath = 'business-edits/api/v1/business-edits';
    if (loginType === 'master') {
      return `${API_BASE_URL}/master/${servicePath}`;
    } else if (loginType === 'admin') {
      return `${API_BASE_URL}/admin/${servicePath}`;
    } else {
      throw new Error('Access denied: Only admin and master users can edit businesses.');
    }
  }, [API_BASE_URL, loginType]);

  const handleApiResponse = async (response) => {
    if (!response.ok) {
      let errorMessage;
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorData.message || `Request failed with status ${response.status}`;
      } catch {
        errorMessage = await response.text() || `Request failed with status ${response.status}`;
      }
      throw new Error(errorMessage);
    }
    return await response.json();
  };

  const clearState = useCallback(() => {
    setError(null);
    setSuccess(null);
  }, []);

  /**
   * Update Business Profile Details
   * Updates: Name, Year, Category, Owner, Email, Phone
   * Excludes: Password
   */
  const updateBusinessProfile = useCallback(async (registryNumber, profileData) => {
    if (!registryNumber) {
      setError('Business registry number is required.');
      return;
    }

    setLoading(true);
    clearState();

    try {
      const baseUrl = getEditsBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/profile`;

      console.log(`Updating business profile for: ${registryNumber}`);

      // Ensure data structure matches backend expectation (BusinessProfileUpdate struct)
      const payload = {
        business_name: profileData.business_name,
        business_year: parseInt(profileData.business_year, 10),
        business_category: profileData.business_category,
        business_owner: profileData.business_owner,
        business_email: profileData.business_email,
        business_phone: profileData.business_phone
      };

      const response = await makeAuthenticatedRequest(url, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      const result = await handleApiResponse(response);

      setSuccess('Business profile updated successfully.');
      console.log('Profile update successful:', result);
      return result;

    } catch (err) {
      const errorMessage = err.message || 'Failed to update business profile.';
      setError(errorMessage);
      console.error('Profile update error:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, getEditsBaseUrl, clearState]);

  /**
   * Update Business Password
   * Updates: password_hash only
   */
  const updateBusinessPassword = useCallback(async (registryNumber, passwordHash) => {
    if (!registryNumber) {
      setError('Business registry number is required.');
      return;
    }

    if (!passwordHash) {
      setError('Password hash is required.');
      return;
    }

    setLoading(true);
    clearState();

    try {
      const baseUrl = getEditsBaseUrl();
      const url = `${baseUrl}/registry/${encodeURIComponent(registryNumber)}/password`;

      console.log(`Updating password for: ${registryNumber}`);

      // Payload matches BusinessPasswordUpdate struct
      const payload = {
        password_hash: passwordHash
      };

      const response = await makeAuthenticatedRequest(url, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });

      const result = await handleApiResponse(response);

      setSuccess('Business password updated successfully.');
      console.log('Password update successful:', result);
      return result;

    } catch (err) {
      const errorMessage = err.message || 'Failed to update business password.';
      setError(errorMessage);
      console.error('Password update error:', errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [makeAuthenticatedRequest, getEditsBaseUrl, clearState]);

  const value = {
    // State
    loading,
    error,
    success,

    // Methods
    updateBusinessProfile,
    updateBusinessPassword,
    clearState,

    // Helper to check access level in UI
    isMasterUser: loginType === 'master'
  };

  return (
    <BusinessEditsContext.Provider value={value}>
      {children}
    </BusinessEditsContext.Provider>
  );
};